import type { GameSnapshot, SceneNode } from '@aod/protocol';
import { predictCombatOutcome as predictCombatOutcomePure } from '../sim/useSimWorld/combatPredictor.js';
import type { SimWorld } from '../sim/useSimWorld/index.js';
import type { AttackToast } from '../ui/AttackToastStack.js';
import type { AodTestBridge, Selection } from './useTestBridge.js';

interface BuildBridgeArgs {
  readonly snapshot: GameSnapshot;
  readonly sim: SimWorld;
  readonly localPlayerIndex: number;
  readonly selection: Selection;
  readonly setSelection: (s: Selection) => void;
  readonly attackToasts: ReadonlyArray<AttackToast>;
}

export function buildBridge(args: BuildBridgeArgs): AodTestBridge {
  const { snapshot, sim, localPlayerIndex, selection, setSelection, attackToasts } = args;
  const all: ReadonlyArray<SceneNode> = snapshot.scene.children ?? [];
  const enemyCity = (): SceneNode | null =>
    all.find(
      (n) => n.type === 'city' && (n.props?.ownerId as number | undefined) !== localPlayerIndex,
    ) ?? null;

  return {
    selectFirstCity: () => selectFirstOwnCity(all, 'city', localPlayerIndex, setSelection),
    selectFirstTile: () => selectFirstByType(all, 'tile', setSelection),
    selectFirstUnit: () => selectFirstCombatUnit(all, setSelection),
    selectFirstUnitByOwner: (ownerId: number) =>
      selectFirstCombatUnitForOwner(all, setSelection, ownerId),
    selectFirstHeroByOwner: (ownerId: number) =>
      selectFirstHeroForOwner(all, setSelection, ownerId),
    clearSelection: () => setSelection(null),
    currentSelectionId: () => selection?.id ?? null,

    currentTick: () => Number(sim.tick),
    currentRunState: () => sim.runState,
    currentSpeed: () => sim.speed,
    pause: () => sim.pause(),
    resume: () => sim.resume(),
    setSpeed: (m: number) => sim.setSpeed(m),
    fastForward: (ticks: number) => sim.fastForward(ticks),

    attackCity: (id: string) => sim.attackCity(id),
    attackEnemyCity: (count = 1) => {
      const enemy = enemyCity();
      if (!enemy) return null;
      for (let i = 0; i < count; i++) sim.attackCity(enemy.id);
      return enemy.id;
    },
    currentVictory: () => sim.victory,

    startNewMatch: (seed?: number) => {
      setSelection(null);
      sim.newMatch(seed);
    },
    currentTreasury: () => sim.localTreasury,

    cityHpForOwner: (ownerId: number) => {
      const city = all.find(
        (n) => n.type === 'city' && (n.props?.ownerId as number | undefined) === ownerId,
      );
      if (!city) return null;
      const current = city.props?.hp as number | undefined;
      const max = city.props?.hpMax as number | undefined;
      return current !== undefined && max !== undefined ? { current, max } : null;
    },
    attackToasts: () => attackToasts,
    unitForOwner: (ownerId: number) => {
      const unit = all.find(
        (n) =>
          n.type === 'unit' &&
          (n.props?.ownerId as number | undefined) === ownerId &&
          !(n.tags as string[] | undefined)?.includes('hero'),
      );
      if (!unit) return null;
      return {
        id: unit.id,
        q: unit.props?.q as number,
        r: unit.props?.r as number,
        kind: (unit.props?.kind as string) ?? 'warrior',
        remainingMovement: (unit.props?.remainingMovement as number | undefined) ?? 0,
        maxMovement: (unit.props?.maxMovement as number | undefined) ?? 1,
      };
    },
    heroForOwner: (ownerId: number) => {
      const hero = all.find(
        (n) =>
          n.type === 'unit' &&
          (n.props?.ownerId as number | undefined) === ownerId &&
          (n.tags as string[] | undefined)?.includes('hero'),
      );
      if (!hero) return null;
      const hp = hero.props?.hero as Record<string, unknown> | undefined;
      return {
        id: hero.id,
        name: (hp?.name as string) ?? '?',
        archetype: (hp?.archetype as string) ?? '?',
        role: (hp?.role as string) ?? '?',
        level: (hp?.level as number) ?? 1,
        loyalty: (hp?.loyalty as number) ?? 50,
        xp: (hp?.xp as number) ?? 0,
        q: (hero.props?.q as number) ?? 0,
        r: (hero.props?.r as number) ?? 0,
        isWounded: (hp?.isWounded as boolean) ?? false,
        isCaptured: (hp?.isCaptured as boolean) ?? false,
        romance: hp?.romance as string | undefined,
        rivalry: (hp?.rivalry as Record<string, unknown>)?.rivalName as string | undefined,
        rivalryIntensity: (hp?.rivalry as Record<string, unknown>)?.intensity as number | undefined,
        inheritedFrom: hp?.inheritedFrom as string | undefined,
      };
    },
    allHeroes: () =>
      all
        .filter((n) => n.type === 'unit' && (n.tags as string[] | undefined)?.includes('hero'))
        .map((n) => {
          const hp = n.props?.hero as Record<string, unknown> | undefined;
          return {
            id: n.id,
            name: (hp?.name as string) ?? '?',
            archetype: (hp?.archetype as string) ?? '?',
            role: (hp?.role as string) ?? '?',
            level: (hp?.level as number) ?? 1,
            loyalty: (hp?.loyalty as number) ?? 50,
            ownerId: (n.props?.ownerId as number) ?? -1,
          };
        }),
    currentUnitCount: () => all.filter((n) => n.type === 'unit').length,
    // Diagnostic: dump EVERY unit node in the scene (hero + combat, all owners).
    allUnitsDump: () =>
      all
        .filter((n) => n.type === 'unit')
        .map((n) => ({
          id: n.id,
          ownerId: n.props?.ownerId as number | undefined,
          q: n.props?.q as number | undefined,
          r: n.props?.r as number | undefined,
          kind: n.props?.kind as string | undefined,
          tags: n.tags as string[] | undefined,
        })),
    garrisonCountForCity: (cityId: string) => {
      const city = all.find((n) => n.id === cityId);
      if (!city) return 0;
      const cq = city.props?.q as number | undefined;
      const cr = city.props?.r as number | undefined;
      const owner = city.props?.ownerId as number | undefined;
      if (cq === undefined || cr === undefined || owner === undefined) return 0;
      return all.filter(
        (n) =>
          n.type === 'unit' &&
          (n.props?.ownerId as number | undefined) === owner &&
          (n.props?.q as number | undefined) === cq &&
          (n.props?.r as number | undefined) === cr,
      ).length;
    },
    cityCoords: (cityId: string) => {
      const city = all.find((n) => n.id === cityId);
      if (!city) return null;
      const cq = city.props?.q as number | undefined;
      const cr = city.props?.r as number | undefined;
      if (cq === undefined || cr === undefined) return null;
      return { q: cq, r: cr };
    },
    deployFirstGarrison: (cityId: string) => {
      const city = all.find((n) => n.id === cityId);
      if (!city) return null;
      const cq = city.props?.q as number | undefined;
      const cr = city.props?.r as number | undefined;
      const owner = city.props?.ownerId as number | undefined;
      if (cq === undefined || cr === undefined || owner === undefined) return null;
      const garrisoned = all.find(
        (n) =>
          n.type === 'unit' &&
          (n.props?.ownerId as number | undefined) === owner &&
          (n.props?.q as number | undefined) === cq &&
          (n.props?.r as number | undefined) === cr,
      );
      if (!garrisoned) return null;
      const dirs: Array<[number, number]> = [
        [1, 0],
        [1, -1],
        [0, -1],
        [-1, 0],
        [-1, 1],
        [0, 1],
      ];
      for (const [dq, dr] of dirs) {
        const tq = cq + dq;
        const tr = cr + dr;
        const blocked = all.some(
          (n) =>
            (n.type === 'city' || n.type === 'unit') &&
            (n.props?.q as number | undefined) === tq &&
            (n.props?.r as number | undefined) === tr,
        );
        if (!blocked) {
          const ok = sim.deployUnit(garrisoned.id, tq, tr);
          return ok ? garrisoned.id : null;
        }
      }
      return null;
    },
    startBuildingForCity: (cityId: string, buildingId: string) =>
      sim.startBuilding(cityId, buildingId),
    cityBuildingProgress: (cityId: string) => {
      const node = all.find((n) => n.id === cityId);
      return (node?.props?.buildingProgress as string | undefined) ?? null;
    },
    allBuildings: () => {
      const globals = snapshot.state.globals as
        | {
            buildings?: ReadonlyArray<{
              id: string;
              name: string;
              description: string;
              cost: number;
            }>;
          }
        | undefined;
      return globals?.buildings ?? [];
    },
    moveHumanUnit: (targetQ: number, targetR: number) => {
      const unit = all.find(
        (n) =>
          n.type === 'unit' &&
          (n.props?.ownerId as number | undefined) === localPlayerIndex &&
          !(n.tags as string[] | undefined)?.includes('hero'),
      );
      if (!unit) return null;
      const ok = sim.moveUnit(unit.id, targetQ, targetR);
      return ok ? unit.id : null;
    },
    predictCombatOutcome: (attackerUnitId: string, defenderUnitId: string) => {
      const attacker = all.find((n) => n.type === 'unit' && n.id === attackerUnitId);
      const defender = all.find((n) => n.type === 'unit' && n.id === defenderUnitId);
      if (!attacker || !defender) return null;
      const dq = defender.props?.q as number | undefined;
      const dr = defender.props?.r as number | undefined;
      const defenderTile =
        dq !== undefined && dr !== undefined
          ? all.find(
              (n) =>
                n.type === 'tile' &&
                (n.props?.q as number | undefined) === dq &&
                (n.props?.r as number | undefined) === dr,
            )
          : undefined;
      const defenderTerrain = (defenderTile?.props?.biome as string | undefined) ?? 'plains';
      return predictCombatOutcomePure({
        attackerKind: (attacker.props?.kind as string | undefined) ?? 'warrior',
        attackerFormation:
          (attacker.props?.formation as string | undefined as Parameters<
            typeof predictCombatOutcomePure
          >[0]['attackerFormation']) ?? 'Standard',
        defenderKind: (defender.props?.kind as string | undefined) ?? 'warrior',
        defenderFormation:
          (defender.props?.formation as string | undefined as Parameters<
            typeof predictCombatOutcomePure
          >[0]['defenderFormation']) ?? 'Standard',
        defenderTerrain,
        attackerHp: (attacker.props?.hp as number | undefined) ?? 0,
        attackerHpMax: (attacker.props?.hpMax as number | undefined) ?? 1,
        defenderHp: (defender.props?.hp as number | undefined) ?? 0,
        defenderHpMax: (defender.props?.hpMax as number | undefined) ?? 1,
      });
    },
  };
}

function selectFirstCombatUnit(
  all: ReadonlyArray<SceneNode>,
  setSelection: (s: Selection) => void,
): string | null {
  const node = all.find(
    (n) => n.type === 'unit' && !(n.tags as string[] | undefined)?.includes('hero'),
  );
  if (!node) return null;
  setSelection({ kind: 'unit', id: node.id });
  return node.id;
}

function selectFirstCombatUnitForOwner(
  all: ReadonlyArray<SceneNode>,
  setSelection: (s: Selection) => void,
  ownerId: number,
): string | null {
  const node = all.find(
    (n) =>
      n.type === 'unit' &&
      (n.props?.ownerId as number | undefined) === ownerId &&
      !(n.tags as string[] | undefined)?.includes('hero'),
  );
  if (!node) return null;
  setSelection({ kind: 'unit', id: node.id });
  return node.id;
}

function selectFirstHeroForOwner(
  all: ReadonlyArray<SceneNode>,
  setSelection: (s: Selection) => void,
  ownerId: number,
): string | null {
  const node = all.find(
    (n) =>
      n.type === 'unit' &&
      (n.props?.ownerId as number | undefined) === ownerId &&
      (n.tags as string[] | undefined)?.includes('hero'),
  );
  if (!node) return null;
  setSelection({ kind: 'unit', id: node.id });
  return node.id;
}

function selectFirstOwnCity(
  all: ReadonlyArray<SceneNode>,
  type: 'city',
  ownerId: number,
  setSelection: (s: Selection) => void,
): string | null {
  const node = all.find(
    (n) =>
      n.type === type &&
      (n.props?.ownerId as number | undefined) === ownerId &&
      n.props?.neutral !== true,
  );
  if (!node) return null;
  setSelection({ kind: type, id: node.id });
  return node.id;
}

function selectFirstByType(
  all: ReadonlyArray<SceneNode>,
  type: 'city' | 'tile' | 'unit',
  setSelection: (s: Selection) => void,
): string | null {
  const node = all.find((n) => n.type === type);
  if (!node) return null;
  setSelection({ kind: type, id: node.id });
  return node.id;
}
